import { createSelector } from 'reselect';
import get from 'lodash/get';
import mapValues from 'lodash/mapValues';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import arrToObj from 'store/utils/transforms';
import { calculateFullNameDotFormat } from 'store/utils/project';

export const getPurgeList = ({ project }) => project.purge.data;
export const getIsOpen = ({ project }) => project.purge.isOpen;
export const getIsLoading = ({ project }) => project.purge.isLoading;
export const getIsPurging = ({ project }) => project.purge.isPurging;

export const getPurgableList = createSelector(getPurgeList, obj =>
  arrToObj(
    Object.entries(obj).map(([key, val]) => ({
      [key]: val.filter(e => e.purgable),
    })),
  ),
);

export const getFinalPurgableData = createSelector(getPurgableList, obj =>
  mapValues(obj, item => map(item, 'id')),
);

export const checkIfPurgableDataAreEmpty = createSelector(
  getPurgeList,
  purgableList =>
    isEmpty(Object.values(purgableList).filter(items => items.length > 0)),
);

export const checkIfAvailablePurgableItemExists = createSelector(
  getPurgeList,
  purgableList =>
    !!Object.values(purgableList).reduce(
      (numberOfAvailablePurgableItems, items) =>
        items.length > 0
          ? numberOfAvailablePurgableItems +
            items.filter(item => item.purgable).length
          : numberOfAvailablePurgableItems,
      0,
    ),
);

export const getProjectName = ({ project }) => project.show.data.name;
export const getProjectSlug = ({ project }) => project.show.data.slug;
export const getProjectAccount = ({ project }) => project.show.data.account;
export const getProject = ({ project }) => project.show.data;
export const getProjectIsEmpty = ({ project }) => isEmpty(project.show.data);
export const getProjectIsLoading = ({ project }) => project.show.isLoading;

export const getIsExporting = ({ project }) => project.export.isLoading;

export const getHasMachineLearningEnabled = createSelector(
  getProject,
  project => get(project, 'ml_enabled', false) === true,
);

export const getHasFlowEnabled = createSelector(
  getProject,
  project => get(project, 'dialog_flow_enabled', false) === true,
);

export const getHasFlowV2Enabled = createSelector(
  getProject,
  project => get(project, 'dialog_flow_v2_enabled', false) === true,
);

export const getGeneralSettingsIsLoading = ({ project }) =>
  project.settings.general.isLoading;

export const getGeneralSettings = ({
  project: {
    show: {
      data: { name },
    },
    settings: { general },
  },
}) => (general.isLoading ? {} : { name, ...general.data });

export const getCreatorFullNameDotFormat = createSelector(getProject, project =>
  calculateFullNameDotFormat(
    get(project.created_by, 'first_name', ''),
    get(project.created_by, 'last_name', ''),
  ),
);

export const getNameIsUpdating = ({ project }) =>
  project.settings.updateName.isLoading;

/* eslint-disable camelcase */
export const getCreatorWithDateLabel = createSelector(
  getProject,
  getCreatorFullNameDotFormat,
  (project, creatorFullNameDotFormat) => {
    const username = get(project.created_by, 'username', '');

    const dateDisplayFormat = moment(project.created_at).format('MMMM DD YYYY');

    return `Created by ${creatorFullNameDotFormat ||
      username} on ${dateDisplayFormat}`;
  },
);
/* eslint-enable camelcase */

export const getSnapshots = ({ project }) =>
  project.settings.snapshots.list.data.items;

export const getSnapshotsIsLoading = ({ project }) =>
  project.settings.snapshots.list.isLoading;

export const getCreateSnapshotIsLoading = ({ project }) =>
  project.settings.snapshots.create.isLoading;

/* eslint-disable camelcase */
export const getSnapshotsGroupedByDate = createSelector(
  getSnapshots,
  snapshots =>
    snapshots.reduce((groups, snapshot) => {
      const createdAtMomentObj = moment(snapshot.created_at);
      let createdAtDisplayFormat;

      if (createdAtMomentObj.isSame(moment(), 'day')) {
        createdAtDisplayFormat = 'TODAY';
      } else {
        createdAtDisplayFormat = createdAtMomentObj
          .format('MMM D, YYYY')
          .toUpperCase();
      }

      if (!groups[createdAtDisplayFormat]) {
        // eslint-disable-next-line no-param-reassign
        groups[createdAtDisplayFormat] = [];
      }

      const creatorFullNameDotFormat =
        calculateFullNameDotFormat(
          get(snapshot, 'created_by.first_name', ''),
          get(snapshot, 'created_by.last_name', ''),
        ) || get(snapshot, 'created_by.username', '');

      const snapshotFormatted = {
        ...snapshot,
        dateDisplayFormat: moment(snapshot.created_at)
          .format('MMM D, YYYY')
          .toUpperCase(),
        timeDisplayFormat: moment(snapshot.created_at).format('HH:mm'),
        creatorFullNameDotFormat,
      };
      groups[createdAtDisplayFormat].push(snapshotFormatted);
      return groups;
    }, {}),
);
/* eslint-enable camelcase */

export const getRollbackIsLoading = ({ project }) =>
  project.settings.snapshots.rollback.isLoading;

export const getIsDeleting = ({ project }) => project.delete.isLoading;

export const getDeleteError = ({ project }) => project.delete.error;

export const getProjectFlowId = createSelector(getProject, project =>
  get(project, 'flow_id'),
);

export const getProjectBranch = ({ project }) =>
  project.show.data.project_branch;

export const getVersionControlInitializationIsLoading = ({ project }) =>
  project.settings.versionControl.isLoading;
